import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { FilterMetadata } from 'primeng/api';
import { Observable, throwError } from 'rxjs';
import { retry, catchError } from 'rxjs/operators';

import { GlobalSettingsData } from 'src/app/shared/global-settings-data'
import { GetglobalsettingsService } from '../services/getglobalsettings.service'
import { SecurityGroups, SecurityGroupMembers } from '../shared/securitygroups';

/**
 * REST client for application security: user access groups, security group
 * definitions, and per-user database settings. Covers the following backend
 * route groups: `/SecurityGroups`, `/SecurityGroupMembers`,
 * `/SecurityGroupMember`.
 *
 * This service has a dual role that is reflected by the existence of two
 * similar update methods ({@link putUserSecuritySettings} and
 * {@link putManagerSettings}): both update the same underlying
 * `SecurityGroupMember` row but accept different DTO shapes —
 * `SecurityGroupMembers` (admin perspective) vs `GlobalSettingsData` (user
 * profile perspective). The duplication is a known design limitation and is
 * preserved to avoid breaking callers.
 *
 * {@link postSearchManagerSettings} is called during session initialisation by
 * {@link UserConfigService} to load the logged-in user's database profile.
 *
 * @remarks
 * All methods apply `retry(1)` on network failure. Errors are surfaced via
 * {@link handleError}, which shows a browser `alert` (legacy pattern flagged
 * for Phase 1 replacement).
 */
@Injectable({
  providedIn: 'root'
})

export class SecureApiService {

  apiURL: string = '';

  constructor(
    private http: HttpClient,
    private globalsettingsservice: GetglobalsettingsService,
  ) {
  }

  /**
   * Builds an absolute API URL by prepending the runtime API server address.
   *
   * @param url - Path starting with `/`.
   * @returns Absolute URL to the backend endpoint.
   */
  private _getApiUrl(url: string = ''): string {
    let _api: string = this.globalsettingsservice.getApiServerUrl();
    return _api + url;
  }

  // ---------------------------------------------------------------------------
  // Access group lookup
  // ---------------------------------------------------------------------------

  /**
   * Returns the numeric access group ID for a given person.
   *
   * Calls `GET /getAccessGroup/:personId`.
   *
   * @param personId - Person ID (`PERSID`) to look up.
   * @returns Observable emitting the access group ID as a number.
   */
  getAccessGroup(personId: number): Observable<number> {
    return this.http.get<number>(this._getApiUrl('/getAccessGroup/' + personId), { headers: this.globalsettingsservice.getStandardApiHeaders() })
      .pipe(
        retry(1),
        catchError(this.handleError)
      )
  }

  // ---------------------------------------------------------------------------
  // Security group definitions
  // ---------------------------------------------------------------------------

  /**
   * Returns all security group definitions.
   *
   * Calls `GET /SecurityGroups/`.
   *
   * @returns Observable emitting an array of `SecurityGroups` records.
   */
  getSecurityGroupList(): Observable<SecurityGroups[]> {
    return this.http.get<SecurityGroups[]>(this._getApiUrl('/SecurityGroups/'), { headers: this.globalsettingsservice.getStandardApiHeaders() })
      .pipe(
        retry(1),
        catchError(this.handleError)
      )
  }

  /**
   * Updates an existing security group definition.
   *
   * Calls `PUT /SecurityGroups/SECGROUPID/:SECGROUPID`. The `SECGROUPID`
   * field is stripped from the body before sending (ID is in the URL only).
   *
   * Prompt 13-J (2026-08-24, closing `SEC-3`): this method's implementation
   * was swapped with {@link postSecurityGroup}'s — before this fix,
   * `putSecurityGroup` called `PUT /SecurityGroups/` and actually performed
   * an *insert*, backwards from what its name says. The backend route pair
   * was inverted the same way (`routes/SecurityGroups.ts`); both sides
   * fixed together so no intermediate deploy state has them disagreeing.
   * Callers were not renamed — only what each name actually does changed.
   *
   * @param gr - The `SecurityGroups` record to update. `SECGROUPID` must be set.
   * @returns Observable emitting the number of rows affected (Prompt 13-F
   *   interim response-contract rule — writes return a row count, not the
   *   entity; re-`GET` for a fresh read).
   */
  putSecurityGroup(gr: SecurityGroups): Observable<number> {
    console.log('*** putSecurityGroup: ');
    environment.production || console.table(gr);
    const { SECGROUPID, ...data } = gr;
    return this.http.put<number>(this._getApiUrl('/SecurityGroups/SECGROUPID/' + SECGROUPID), data, { headers: this.globalsettingsservice.getStandardApiHeaders() })
      .pipe(
        retry(1),
        catchError(this.handleError)
      )
  }

  /**
   * Updates an existing security group definition.
   *
   * Calls `POST /SecurityGroups/`. The caller supplies `SECGROUPID` in the
   * body — unlike `Client`/`Person`/`ProdGroup`'s create routes, the backend
   * doesn't derive a new ID via `MAX(SECGROUPID)+1` here either; whatever ID
   * this call sends is inserted as-is. No server-assigned ID to hand back.
   *
   * Prompt 13-J (2026-08-24, closing `SEC-3`): this method's implementation
   * was swapped with {@link putSecurityGroup}'s — see that method's doc
   * comment for the full context.
   *
   * @param gr - The `SecurityGroups` record to create.
   * @returns Observable emitting the number of rows affected (Prompt 13-F
   *   interim response-contract rule; no server-assigned ID to return here,
   *   since the caller already chose `SECGROUPID`).
   */
  postSecurityGroup(gr: SecurityGroups): Observable<number> {
    console.log('*** postSecurityGroup: ');
    environment.production || console.table(gr);
    return this.http.post<number>(this._getApiUrl('/SecurityGroups/'), gr, { headers: this.globalsettingsservice.getStandardApiHeaders() })
      .pipe(
        retry(1),
        catchError(this.handleError)
      )
  }

  // ---------------------------------------------------------------------------
  // Security group members (user access assignments)
  // ---------------------------------------------------------------------------

  /**
   * Returns all security group member records for a given company.
   *
   * Calls `GET /SecurityGroupMembers/:compId`.
   *
   * @param compId - Company client ID (`CLIID`) to scope the query.
   * @returns Observable emitting an array of `SecurityGroupMembers` records.
   */
  getUsersAccessGroup(compId: number): Observable<SecurityGroupMembers[]> {
    return this.http.get<SecurityGroupMembers[]>(this._getApiUrl('/SecurityGroupMembers/' + compId), { headers: this.globalsettingsservice.getStandardApiHeaders() })
      .pipe(
        retry(1),
        catchError(this.handleError)
      )
  }

  /**
   * @deprecated Uses GET instead of POST despite the method name. Retained for
   *   compatibility. Use {@link getUsersAccessGroup} instead.
   */
  postUsersAccessGroup(compId: number): Observable<SecurityGroupMembers[]> {
    return this.http.get<SecurityGroupMembers[]>(this._getApiUrl('/SecurityGroupMembers/' + compId), { headers: this.globalsettingsservice.getStandardApiHeaders() })
      .pipe(
        retry(1),
        catchError(this.handleError)
      )
  }

  /**
   * Searches for a user's database profile by Keycloak subject (`sub`).
   *
   * Calls `POST /SecurityGroupMember/search`. Used by {@link UserConfigService}
   * during session initialisation to load the logged-in user's
   * `GlobalSettingsData`.
   *
   * @param login - Object containing the Keycloak `sub` (user UUID).
   * @returns Observable emitting a one-element `GlobalSettingsData[]` array,
   *   or an empty array if the user has no database profile yet.
   */
  postSearchManagerSettings(login: any): Observable<GlobalSettingsData[]> {
    console.log('*** postSearchManagerSettings: ' + JSON.stringify(login));
    return this.http.post<GlobalSettingsData[]>(this._getApiUrl('/SecurityGroupMember/search'), login, { headers: this.globalsettingsservice.getStandardApiHeaders() })
      .pipe(
        retry(1),
        catchError(this.handleError)
      )
  }

  /**
   * Creates or re-assigns a security group member record when a user's login
   * identity changes (e.g. migrated to a different Keycloak realm or email).
   *
   * Calls `POST /SecurityGroupMember/SGMGROUPID/:SGMGROUPID/...`.
   * The new login is passed as `NEWLOGIN`; the original identity fields
   * (`SGMGROUPID`, `SGMCLIID`, `SGMPERSID`, `SGMLOGIN`) are encoded in the URL.
   *
   * @param ori - The existing `SecurityGroupMembers` record (provides URL keys).
   * @param data - The updated member data including the new `SGMLOGIN`.
   * @returns Observable emitting the number of rows affected.
   */
  postUserSecuritySettings(ori: SecurityGroupMembers, data: SecurityGroupMembers): Observable<number> {
    environment.production || console.log('*** postUserSecuritySettings: ');
    environment.production || console.table(data);
    const { SGMGROUPID, SGMCLIID, SGMPERSID, SGMLOGIN, ...rest } = ori;
    const { SGMLOGIN: dataSGMLOGIN } = data;
    return this.http.post<number>(this._getApiUrl('/SecurityGroupMember/SGMGROUPID/' + SGMGROUPID + '/SGMCLIID/' + SGMCLIID + '/SGMPERSID/' + SGMPERSID + '/?LOGIN=' + encodeURIComponent(SGMLOGIN) + '&NEWLOGIN=' + encodeURIComponent(dataSGMLOGIN)), data, { headers: this.globalsettingsservice.getStandardApiHeaders() })
      .pipe(
        retry(1),
        catchError(this.handleError)
      )
  }

  /**
   * Deletes a security group member record, removing the user's access to the
   * system.
   *
   * Calls `DELETE /SecurityGroupMember/SGMGROUPID/:SGMGROUPID/...?LOGIN=...`.
   * All four identity fields are encoded in the URL.
   *
   * @param ori - The `SecurityGroupMembers` record to delete.
   * @returns Observable emitting an array with the number of rows affected.
   */
  deleteUserSecuritySettings(ori: SecurityGroupMembers): Observable<number[]> {
    environment.production || console.log('*** deleteUserSecuritySettings: ');
    environment.production || console.table(ori);
    const { SGMGROUPID, SGMCLIID, SGMPERSID, SGMLOGIN, ...rest } = ori;
    return this.http.delete<number[]>(this._getApiUrl('/SecurityGroupMember/SGMGROUPID/' + SGMGROUPID + '/SGMCLIID/' + SGMCLIID + '/SGMPERSID/' + SGMPERSID + '/?LOGIN=' + encodeURIComponent(SGMLOGIN)), { headers: this.globalsettingsservice.getStandardApiHeaders() })
      .pipe(
        retry(1),
        catchError(this.handleError)
    )
  }

  /**
   * Updates a security group member record using the admin-facing
   * `SecurityGroupMembers` DTO.
   *
   * Calls `PUT /SecurityGroupMember/SGMGROUPID/:id/SGMCLIID/:id/SGMPERSID/:id`.
   *
   * @remarks
   * This method and {@link putManagerSettings} update the same database row
   * but accept different DTO shapes — `SecurityGroupMembers` for admin flows
   * and `GlobalSettingsData` for user-profile flows. The duplication is a
   * known design limitation.
   *
   * @param data - The `SecurityGroupMembers` record to update.
   * @returns Observable emitting the number of rows affected.
   */
  putUserSecuritySettings(data: SecurityGroupMembers): Observable<number> {
    console.log('*** postUserSecuritySettings: ');
    environment.production || console.table(data);
    let accgId: number = data.SGMGROUPID;
    let persid: number = data.SGMPERSID;
    let compId: number = data.SGMCLIID;
    console.log(accgId);
    return this.http.put<number>(this._getApiUrl('/SecurityGroupMember/SGMGROUPID/' + accgId + '/SGMCLIID/' + compId + '/SGMPERSID/' + persid), data, { headers: this.globalsettingsservice.getStandardApiHeaders() })
      .pipe(
        retry(1),
        catchError(this.handleError)
      )
  }

  /**
   * Updates a security group member record using the user-profile-facing
   * `GlobalSettingsData` DTO.
   *
   * Calls `PUT /SecurityGroupMember/SGMGROUPID/:id/SGMCLIID/:id/SGMPERSID/:id`.
   *
   * @remarks
   * See {@link putUserSecuritySettings} for the design note about duplication.
   *
   * @param data - The `GlobalSettingsData` containing the updated profile.
   * @returns Observable emitting the number of rows affected (Prompt 13-F,
   *   interim response-contract rule — see
   *   `docs/PROPOSAL_rest-contract-alignment.md` in the API repo: writes
   *   return a row count, not the entity; re-`GET` for a fresh read). No
   *   call sites in this app as of this writing — confirmed via a
   *   repo-wide grep.
   */
  putManagerSettings(data: GlobalSettingsData): Observable<number> {
    console.log('*** postSearchManagerSettings: ' + JSON.stringify(data));
    let persid: number = data.mainPersonId;
    let compId: number = data.mainCompanyId;
    let accgId: number = data.accessGroup;
    return this.http.put<number>(this._getApiUrl('/SecurityGroupMember/SGMGROUPID/' + accgId + '/SGMCLIID/' + compId + '/SGMPERSID/' + persid), data, { headers: this.globalsettingsservice.getStandardApiHeaders() })
      .pipe(
        retry(1),
        catchError(this.handleError)
      )
  }

  /**
   * Updates a security group member record for a user, supporting multiple
   * logins (e.g. different Keycloak realms) via the `LOGIN` query parameter.
   *
   * Calls `POST /SecurityGroupMember/SGMGROUPID/:id/...?LOGIN=<login>`.
   * Uses `SecurityGroupMembers.mapGlobalSettings()` to convert the
   * `GlobalSettingsData` shape to the member DTO before posting.
   *
   * Despite the `POST` verb, the backend route this calls
   * (`webyto_api-express4-TS-20250331/routes/SecurityGroupMember.ts`)
   * updates an existing row matched by the composite key in the URL — not
   * a create — same as {@link postUserSecuritySettings}, which shares this
   * exact backend route.
   *
   * @param data - The `GlobalSettingsData` for the member to update.
   * @returns Observable emitting the number of rows affected (Prompt 13-F
   *   interim response-contract rule, see `putManagerSettings`). Sole call
   *   site (`setup.component.ts`) only logs the emission via `finalize()`,
   *   never reads a field off it — safe under this retype, confirmed
   *   2026-08-23.
   */
  postManagerSettings(data: GlobalSettingsData): Observable<number> {
    console.log('*** postSearchManagerSettings: ' + JSON.stringify(data));
    let persid: number = data.mainPersonId;
    let compId: number = data.mainCompanyId;
    let accgId: number = data.accessGroup;
    // LOGIN allows one user to have multiple logins from different auth servers.
    // Required in both postManagerSettings and postUserSecuritySettings.
    let LOGIN: string = data.SGMLOGIN;
    return this.http.post<number>(this._getApiUrl('/SecurityGroupMember/SGMGROUPID/' + accgId + '/SGMCLIID/' + compId + '/SGMPERSID/' + persid + '/?LOGIN=' + encodeURIComponent(LOGIN)), SecurityGroupMembers.mapGlobalSettings( data), { headers: this.globalsettingsservice.getStandardApiHeaders() })
      .pipe(
        retry(1),
        catchError(this.handleError)
      )
  }

  // ---------------------------------------------------------------------------
  // Error handling
  // ---------------------------------------------------------------------------

  /**
   * Centralised HTTP error handler used by all methods in this service.
   *
   * Extracts a human-readable message, shows a browser `alert` (legacy pattern
   * — Phase 1 will replace with a toast), and re-throws so the calling
   * Observable terminates.
   *
   * @param error - Angular `HttpErrorResponse` or `ErrorEvent`.
   * @returns An Observable that immediately errors with the formatted message.
   */
  handleError(error) {
    let errorMessage = '';
    if (error.error instanceof ErrorEvent) {
      // Get client-side error
      errorMessage = error.error.message;
    } else {
      // Get server-side error
      errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;
    }
    window.alert(errorMessage);
    return throwError(() => errorMessage);
  }
}
